import { Box, Button, CircularProgress } from '@mui/material'
import TextFieldNN from 'components/TextFieldNN'
import PasswordInput from 'components/PasswordInput'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { InvitationData } from 'pages/Invitation'

type Props = {
  data: InvitationData
  acceptInvitation: (formFields: { firstName: string; lastName: string; password: string }) => Promise<void>
}

const InvitationForm: React.FC<Props> = ({ data, acceptInvitation }) => {
  const { user } = data

  const [firstName, setFirstName] = useState({ value: '', error: false, touched: false })
  const [lastName, setLastName] = useState({ value: '', error: false, touched: false })
  const [password, setPassword] = useState({ value: '', error: false })

  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!user) return
    setFirstName((state) => ({ ...state, value: user.firstName }))
    setLastName((state) => ({ ...state, value: user.lastName }))
    setLoading(false)
  }, [user])

  const validateFirstName = useCallback(() => {
    const isValid = firstName.value.length > 0
    setFirstName((message) => ({ ...message, error: !isValid }))
    return isValid
  }, [firstName.value])

  const validateLastName = useCallback(() => {
    const isValid = lastName.value.length > 0
    setLastName((message) => ({ ...message, error: !isValid }))
    return isValid
  }, [lastName.value])

  const isFormValid = useMemo(() => {
    const validFirstName = validateFirstName()
    const validLastName = validateLastName()
    const validPassword = !password.error
    return validFirstName && validLastName && validPassword
  }, [validateFirstName, validateLastName, password.error])

  const onSubmit = async () => {
    if (!isFormValid || !user) return
    setLoading(true)
    await acceptInvitation({ firstName: firstName.value, lastName: lastName.value, password: password.value })
    setLoading(false)
  }

  return (
    <Box mb="16px">
      <TextFieldNN
        id="first-name"
        label="First name"
        placeholder="John"
        value={firstName.value}
        onChange={(e) => setFirstName((state) => ({ ...state, value: e.target.value, error: false }))}
        onFocus={() => setFirstName((state) => ({ ...state, touched: false }))}
        onBlur={() => setFirstName((state) => ({ ...state, touched: true }))}
        error={firstName.touched && firstName.error}
        helperText="First name is required"
        autoComplete="given-name"
      />

      <TextFieldNN
        id="last-name"
        label="Last name"
        placeholder="Doe"
        value={lastName.value}
        onChange={(e) => setLastName((state) => ({ ...state, value: e.target.value, error: false }))}
        onFocus={() => setFirstName((state) => ({ ...state, touched: false }))}
        onBlur={() => setLastName((state) => ({ ...state, touched: true }))}
        error={lastName.touched && lastName.error}
        helperText="Last name is required"
        autoComplete="family-name"
      />

      <TextFieldNN id="email" label="Email" type="email" value={user?.email || ''} disabled />

      <PasswordInput
        label="Password"
        onChange={(state) => {
          setPassword({
            value: state.value,
            error: !state.isValid,
          })
        }}
        autoComplete="new-password"
        onKeyDownEnter={() => {
          if (isFormValid && !loading) onSubmit()
        }}
      />

      <Button variant="contained" fullWidth disabled={!isFormValid || loading} onClick={onSubmit} sx={{ mt: '16px' }}>
        {loading ? <CircularProgress size="20px" /> : 'OK'}
      </Button>
    </Box>
  )
}

export default InvitationForm
